import type { ScreenGuide, ScreenGuideLoader } from './guide';
import type { ScreenManifest } from './types';

export interface ScreenRegistry {
  readonly screens: readonly ScreenManifest[];
  byId(id: string): ScreenManifest | undefined;
  byPath(path: string): ScreenManifest | undefined;
  /** A tela tem guia (U46) ligado no registro. */
  hasGuide(id: string): boolean;
  /** Carrega o guia sob demanda, com o catálogo do módulo; `undefined` se a tela não tem guia. */
  loadGuide(id: string): Promise<ScreenGuide | undefined>;
}

export interface ScreenRegistryOptions {
  /** Id da tela → `load<Mod>Guides` do módulo (U46). Fica fora do JS inicial. */
  readonly guides?: Readonly<Record<string, ScreenGuideLoader>>;
}

function matches(pattern: string, path: string): boolean {
  const re = new RegExp(`^${pattern.replace(/\$[A-Za-z]+/g, '[^/]+').replace(/\/$/, '')}/?$`);
  return re.test(path);
}

export function createScreenRegistry(screens: readonly ScreenManifest[], options: ScreenRegistryOptions = {}): ScreenRegistry {
  const ids = new Set<string>();
  for (const screen of screens) {
    if (ids.has(screen.id)) throw new Error(`tela registrada duas vezes: ${screen.id}`);
    ids.add(screen.id);
  }
  const guides = options.guides ?? {};
  const hasGuide = (id: string) => ids.has(id) && Object.hasOwn(guides, id);
  return {
    screens,
    byId: (id) => screens.find((s) => s.id === id),
    byPath: (path) => screens.find((s) => matches(s.path, path)),
    hasGuide,
    async loadGuide(id) {
      const load = hasGuide(id) ? guides[id] : undefined;
      if (!load) return undefined;
      const module = await load();
      await module.loadMessages();
      return module.screenGuides.find((g) => g.screenId === id);
    },
  };
}

/** Máximo da trilha: seção › lista › detalhe › sub-tela (a aba não conta). */
export const MAX_TRAIL = 4;

/** Telas da raiz até `screen` (inclusive). */
export function screenTrail(registry: ScreenRegistry, screen: ScreenManifest): ScreenManifest[] {
  const trail: ScreenManifest[] = [];
  for (let at: ScreenManifest | undefined = screen; at; at = at.place.parent ? registry.byId(at.place.parent) : undefined) {
    if (trail.includes(at)) break;
    trail.unshift(at);
  }
  return trail;
}

/** Seção do menu da tela: a da raiz da trilha (U42). A regra de acesso da seção sai daqui. */
export function sectionOf(registry: ScreenRegistry, screen: ScreenManifest): string | undefined {
  return screenTrail(registry, screen)[0]?.place.section;
}

/** Hierarquia do registro inteiro (U42): mãe existe, sem ciclo, profundidade ≤ MAX_TRAIL. */
export function validateHierarchy(registry: ScreenRegistry): string[] {
  const issues: string[] = [];
  for (const screen of registry.screens) {
    const parent = screen.place.parent;
    if (parent && !registry.byId(parent)) issues.push(`${screen.id}: tela-mãe ${parent} não registrada`);
    const seen = new Set<string>();
    for (let at: ScreenManifest | undefined = screen; at; at = at.place.parent ? registry.byId(at.place.parent) : undefined) {
      if (seen.has(at.id)) {
        issues.push(`${screen.id}: ciclo na hierarquia`);
        break;
      }
      seen.add(at.id);
    }
    const depth = screenTrail(registry, screen).length + (screenTrail(registry, screen)[0]?.place.section ? 1 : 0);
    if (depth > MAX_TRAIL) issues.push(`${screen.id}: trilha com ${String(depth)} níveis (máximo ${String(MAX_TRAIL)})`);
  }
  return issues;
}
